import { API, graphqlOperation } from "aws-amplify";
import * as mutations from "../graphql/mutations";
import * as queries from "../graphql/queries";

import { AppType } from "../utils/constants";
import { logError } from "../utils/appUtils";

// ##############################################################################
// UPDATE GROUP NAME FUNCTIONS
// ##############################################################################
export const updateGroupName = async (reloadYardId, newGrpupName, notify) => {
  console.clear();

  // Validate user is ADMIN
  if (window.appUser?.appType !== AppType.ADMIN) {
    notify("INVALID YARD UPDATE ATTEMPT - NOT ADMIN", "error");
    return false;
  }

  if (!reloadYardId || !newGrpupName) {
    notify("Missing required inputs (reloadYardId / newGrpupName)", "error");
    return false;
  }

  // Fetch the reload yard record
  let reloadYardRecord;
  try {
    const yardResponse = await API.graphql(
      graphqlOperation(queries.getReloadYard, { id: reloadYardId }),
    );
    reloadYardRecord = yardResponse.data.getReloadYard;

    if (!reloadYardRecord) {
      notify("Reload yard not found", "error");
      return false;
    }
  } catch (err) {
    logError("admin:updateGroupName:fetchYard", err, notify);
    notify("Failed to fetch reload yard", "error");
    return false;
  }

  const oldGroupName = reloadYardRecord?.groups?.[0];
  if (!oldGroupName || !Array.isArray(reloadYardRecord?.groups)) {
    notify(
      "ReloadYard.groups is missing; cannot derive oldGroupName.",
      "error",
    );
    return false;
  }
  if (reloadYardRecord.groups.length !== 1) {
    notify(
      `ReloadYard.groups must have exactly 1 entry (found ${reloadYardRecord.groups.length}).`,
      "error",
    );
    return false;
  }
  if (oldGroupName === newGrpupName) {
    notify("Old and new group names are the same - nothing to do.", "info");
    return true;
  }

  const replaceGroups = (groups) => {
    const list = Array.isArray(groups) ? groups : [];
    if (!list.includes(oldGroupName)) return null;
    const next = list.map((g) => (g === oldGroupName ? newGrpupName : g));
    return [...new Set(next)];
  };

  const listAll = async ({ query, listKey, filter }) => {
    const items = [];
    let nextToken = null;
    do {
      const response = await API.graphql(
        graphqlOperation(query, { filter, limit: 1000, nextToken }),
      );
      const page = response?.data?.[listKey];
      items.push(...(page?.items || []));
      nextToken = page?.nextToken || null;
    } while (nextToken);
    return items;
  };

  const updateMany = async ({
    name,
    query,
    listKey,
    updateMutation,
    filter,
  }) => {
    if (!query || !listKey || !updateMutation) {
      console.log(
        `Update ${name}: SKIP (missing query/listKey/updateMutation)`,
      );
      return { scanned: 0, updated: 0 };
    }

    const items = await listAll({ query, listKey, filter });
    let updated = 0;

    await Promise.allSettled(
      items.map((item) => {
        const nextGroups = replaceGroups(item?.groups);
        if (!nextGroups) return null;
        updated += 1;
        const input = { id: item.id, groups: nextGroups };
        return API.graphql(graphqlOperation(updateMutation, { input }));
      }),
    );

    console.log(`Update ${name}: scanned=${items.length} updated=${updated}`);
    return { scanned: items.length, updated };
  };

  console.log(
    `Starting group update for yard: ${reloadYardRecord.name} (${reloadYardId})`,
  );

  // Only yard-scoped models that have a [groups] field should be included here.
  const resources = [
    {
      name: "ReloadYard",
      query: queries.listReloadYards,
      listKey: "listReloadYards",
      updateMutation: mutations.updateReloadYard,
      filter: { id: { eq: reloadYardId } },
    },
    {
      name: "Accounts",
      query: queries.listAccounts,
      listKey: "listAccounts",
      updateMutation: mutations.updateAccount,
      filter: { reloadYardId: { eq: reloadYardId } },
    },
    {
      name: "Users",
      query: queries.listUsers,
      listKey: "listUsers",
      updateMutation: mutations.updateUser,
      filter: { reloadYardId: { eq: reloadYardId } },
    },

    {
      name: "Areas",
      query: queries.listAreas,
      listKey: "listAreas",
      updateMutation: mutations.updateArea,
      filter: { reloadYardId: { eq: reloadYardId } },
    },
    {
      name: "Zones",
      query: queries.listZones,
      listKey: "listZones",
      updateMutation: mutations.updateZone,
      filter: { reloadYardId: { eq: reloadYardId } },
    },
    {
      name: "Aisles",
      query: queries.listAisles,
      listKey: "listAisles",
      updateMutation: mutations.updateAisle,
      filter: { reloadYardId: { eq: reloadYardId } },
    },
    {
      name: "Locations",
      query: queries.listLocations,
      listKey: "listLocations",
      updateMutation: mutations.updateLocation,
      filter: { reloadYardId: { eq: reloadYardId } },
    },

    {
      name: "ReloadYardCommoditys",
      query: queries.listReloadYardCommoditys,
      listKey: "listReloadYardCommoditys",
      updateMutation: mutations.updateReloadYardCommodity,
      filter: { reloadYardId: { eq: reloadYardId } },
    },
    {
      name: "ReloadYardCapabilitys",
      query: queries.listReloadYardCapabilitys,
      listKey: "listReloadYardCapabilitys",
      updateMutation: mutations.updateReloadYardCapability,
      filter: { reloadYardId: { eq: reloadYardId } },
    },

    {
      name: "Products",
      query: queries.listProducts,
      listKey: "listProducts",
      updateMutation: mutations.updateProduct,
      filter: { reloadYardId: { eq: reloadYardId } },
    },
    {
      name: "Inventories",
      query: queries.listInventories,
      listKey: "listInventories",
      updateMutation: mutations.updateInventorie,
      filter: { reloadYardId: { eq: reloadYardId } },
    },
    {
      name: "InventoryItems",
      query: queries.listInventoryItems,
      listKey: "listInventoryItems",
      updateMutation: mutations.updateInventoryItem,
      filter: { reloadYardId: { eq: reloadYardId } },
    },

    {
      name: "InboundShipments",
      query: queries.listInboundShipments,
      listKey: "listInboundShipments",
      updateMutation: mutations.updateInboundShipment,
      filter: { reloadYardId: { eq: reloadYardId } },
    },
    {
      name: "OutboundShipments",
      query: queries.listOutboundShipments,
      listKey: "listOutboundShipments",
      updateMutation: mutations.updateOutboundShipment,
      filter: { reloadYardId: { eq: reloadYardId } },
    },
    {
      name: "ShipmentLineItems",
      query: queries.listShipmentLineItems,
      listKey: "listShipmentLineItems",
      updateMutation: mutations.updateShipmentLineItem,
      filter: { reloadYardId: { eq: reloadYardId } },
    },
    {
      name: "ShipmentLineItemInventoryItems",
      query: queries.listShipmentLineItemInventoryItems,
      listKey: "listShipmentLineItemInventoryItems",
      updateMutation: mutations.updateShipmentLineItemInventoryItem,
      filter: { reloadYardId: { eq: reloadYardId } },
    },

    {
      name: "Invoices",
      query: queries.listInvoices,
      listKey: "listInvoices",
      updateMutation: mutations.updateInvoice,
      filter: { reloadYardId: { eq: reloadYardId } },
    },
    {
      name: "InvoiceServices",
      query: queries.listInvoiceServices,
      listKey: "listInvoiceServices",
      updateMutation: mutations.updateInvoiceService,
      filter: { reloadYardId: { eq: reloadYardId } },
    },

    {
      name: "Services",
      query: queries.listServices,
      listKey: "listServices",
      updateMutation: mutations.updateService,
      filter: { reloadYardId: { eq: reloadYardId } },
    },
    {
      name: "ChargeRules",
      query: queries.listChargeRules,
      listKey: "listChargeRules",
      updateMutation: mutations.updateChargeRule,
      filter: { reloadYardId: { eq: reloadYardId } },
    },
    {
      name: "ApiKeys",
      query: queries.listApiKeys,
      listKey: "listApiKeys",
      updateMutation: mutations.updateApiKey,
      filter: { reloadYardId: { eq: reloadYardId } },
    },
    {
      name: "PaymentTerms",
      query: queries.listPaymentTerms,
      listKey: "listPaymentTerms",
      updateMutation: mutations.updatePaymentTerm,
      filter: { reloadYardId: { eq: reloadYardId } },
    },
    {
      name: "Timeslots",
      query: queries.listTimeslots,
      listKey: "listTimeslots",
      updateMutation: mutations.updateTimeslot,
      filter: { reloadYardId: { eq: reloadYardId } },
    },
    {
      name: "Tasks",
      query: queries.listTasks,
      listKey: "listTasks",
      updateMutation: mutations.updateTask,
      filter: { reloadYardId: { eq: reloadYardId } },
    },
    {
      name: "Audits",
      query: queries.listAudits,
      listKey: "listAudits",
      updateMutation: mutations.updateAudit,
      filter: { reloadYardId: { eq: reloadYardId } },
    },

    {
      name: "Files",
      query: queries.listFiles,
      listKey: "listFiles",
      updateMutation: mutations.updateFile,
      filter: { reloadYardId: { eq: reloadYardId } },
    },
    {
      name: "Photos",
      query: queries.listPhotos,
      listKey: "listPhotos",
      updateMutation: mutations.updatePhoto,
      filter: { reloadYardId: { eq: reloadYardId } },
    },
    {
      name: "HistoryNotes",
      query: queries.listHistoryNotes,
      listKey: "listHistoryNotes",
      updateMutation: mutations.updateHistoryNote,
      filter: { reloadYardId: { eq: reloadYardId } },
    },

    {
      name: "Seals",
      query: queries.listSeals,
      listKey: "listSeals",
      updateMutation: mutations.updateSeal,
      filter: { reloadYardId: { eq: reloadYardId } },
    },
    {
      name: "TrackCars",
      query: queries.listTrackCars,
      listKey: "listTrackCars",
      updateMutation: mutations.updateTrackCar,
      filter: { reloadYardId: { eq: reloadYardId } },
    },
    {
      name: "Barcodes",
      query: queries.listBarcodes,
      listKey: "listBarcodes",
      updateMutation: mutations.updateBarcode,
      filter: { reloadYardId: { eq: reloadYardId } },
    },
    {
      name: "Tags",
      query: queries.listTags,
      listKey: "listTags",
      updateMutation: mutations.updateTag,
      filter: { reloadYardId: { eq: reloadYardId } },
    },
    {
      name: "LocationTags",
      query: queries.listLocationTags,
      listKey: "listLocationTags",
      updateMutation: mutations.updateLocationTag,
      filter: { reloadYardId: { eq: reloadYardId } },
    },

    {
      name: "CustomerReloadYardAccounts",
      query: queries.listCustomerReloadYardAccounts,
      listKey: "listCustomerReloadYardAccounts",
      updateMutation: mutations.updateCustomerReloadYardAccount,
      filter: { reloadYardId: { eq: reloadYardId } },
    },
    {
      name: "SavedAddresss",
      query: queries.listSavedAddresss,
      listKey: "listSavedAddresss",
      updateMutation: mutations.updateSavedAddress,
      filter: { reloadYardId: { eq: reloadYardId } },
    },

    {
      name: "CarrierUserReloadYardCarriers",
      query: queries.listCarrierUserReloadYardCarriers,
      listKey: "listCarrierUserReloadYardCarriers",
      updateMutation: mutations.updateCarrierUserReloadYardCarrier,
      filter: { reloadYardId: { eq: reloadYardId } },
    },
    {
      name: "WorkChargeRules",
      query: queries.listWorkChargeRules,
      listKey: "listWorkChargeRules",
      updateMutation: mutations.updateWorkChargeRule,
      filter: { reloadYardId: { eq: reloadYardId } },
    },
    {
      name: "ZapierHooks",
      query: queries.listZapierHooks,
      listKey: "listZapierHooks",
      updateMutation: mutations.updateZapierHook,
      filter: { reloadYardId: { eq: reloadYardId } },
    },
    {
      name: "ZapierActions",
      query: queries.listZapierActions,
      listKey: "listZapierActions",
      updateMutation: mutations.updateZapierAction,
      filter: { reloadYardId: { eq: reloadYardId } },
    },
  ];

  let totalScanned = 0;
  let totalUpdated = 0;
  for (const r of resources) {
    try {
      const { scanned, updated } = await updateMany(r);
      totalScanned += scanned;
      totalUpdated += updated;
    } catch (err) {
      logError(`admin:updateGroupName:${r.name}`, err, notify);
    }
  }

  notify(
    `Group update complete. Updated ${totalUpdated} records (scanned ${totalScanned}).`,
    "info",
  );
  return true;
};
